import { useEffect, useState } from 'react'
import { formatMoney } from '../lib/format.js'

// Modal for adding or editing a roster assignment.
//
// Two modes:
//   CREATE — pass `onCreate` + `shiftDate`. Empty form, submit
//            inserts a new row.
//   EDIT   — pass `onUpdate` + `assignment`. Form pre-fills from
//            the existing row; submit calls onUpdate(id, patch).
//            In edit mode the manager can also change status
//            (scheduled / called_out / no_show / completed /
//            cancelled), and a Remove button lets them delete
//            without going back to the list.
//
// Used by both the Daily Huddle (today's roster, create-only) and
// the Schedule screen (any date, both modes).
export default function RosterAssignmentModal({
  open,
  onClose,
  onCreate,         // (payload) → Promise<row>     create mode
  onUpdate,         // (id, patch) → Promise        edit mode
  onDelete,         // (id) → Promise               edit mode (optional)
  activeStaff,      // [{ id, name, role, hourly_wage }, ...]
  shiftDate,        // YYYY-MM-DD string for create mode
  assignment,       // optional; if present, edit mode
}) {
  const isEditMode = !!assignment
  const [form, setForm] = useState(emptyForm())
  const [submitting, setSubmitting] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!open) return
    if (assignment) {
      setForm({
        staff_id:   assignment.staff?.id ?? '',
        start_time: (assignment.start_time || '').slice(0, 5),
        end_time:   (assignment.end_time   || '').slice(0, 5),
        role:       assignment.role  ?? '',
        notes:      assignment.notes ?? '',
        status:     assignment.status ?? 'scheduled',
      })
    } else {
      setForm(emptyForm())
    }
    setError(null)
    setSubmitting(false)
    setDeleting(false)
  }, [open, shiftDate, assignment])

  if (!open) return null

  // Find the picked staff so we can show their wage + role hint inline.
  const pickedStaff = activeStaff.find((s) => s.id === form.staff_id) ?? null

  // Estimated cost for the form's start/end times so the manager sees
  // the labor impact in real time as they type.
  const estCost = (() => {
    if (!pickedStaff?.hourly_wage || !form.start_time || !form.end_time) return null
    const [sh, sm] = form.start_time.split(':').map(Number)
    const [eh, em] = form.end_time.split(':').map(Number)
    let endMin = eh * 60 + em
    const startMin = sh * 60 + sm
    if (endMin < startMin) endMin += 24 * 60
    const hours = Math.max(0, (endMin - startMin) / 60)
    return hours * Number(pickedStaff.hourly_wage)
  })()

  function set(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function handleSubmit(e) {
    e?.preventDefault?.()
    if (submitting || deleting) return
    if (!form.staff_id) {
      setError('Pick a staff member.')
      return
    }
    if (!form.start_time || !form.end_time) {
      setError('Set both start and end times.')
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      if (isEditMode) {
        await onUpdate(assignment.id, {
          staff_id:   form.staff_id,
          start_time: form.start_time,
          end_time:   form.end_time,
          role:       form.role.trim() || null,
          notes:      form.notes.trim() || null,
          status:     form.status,
        })
      } else {
        await onCreate({
          staff_id:   form.staff_id,
          shift_date: shiftDate,
          start_time: form.start_time,
          end_time:   form.end_time,
          role:       form.role.trim() || null,
          notes:      form.notes.trim() || null,
        })
      }
      onClose?.()
    } catch (e2) {
      // eslint-disable-next-line no-console
      console.error('[RosterAssignmentModal] save failed', e2)
      setError(isEditMode ? 'Could not save changes. Try again.' : 'Could not add to roster. Try again.')
    } finally {
      // Always reset — see StaffEditModal for the same bug pattern.
      setSubmitting(false)
    }
  }

  async function handleDelete() {
    if (submitting || deleting || !isEditMode || !onDelete) return
    const ok = window.confirm(
      `Remove this shift?\n\n${assignment.staff?.name ?? 'Unknown'} on ${assignment.shift_date}\n\nThis cannot be undone.`
    )
    if (!ok) return
    setDeleting(true)
    setError(null)
    try {
      await onDelete(assignment.id)
      onClose?.()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[RosterAssignmentModal] delete failed', e)
      setError('Could not remove. Try again.')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      <form
        onSubmit={handleSubmit}
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label={isEditMode ? 'Edit shift' : 'Add shift'}
      >
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium">
              {isEditMode
                ? `Edit shift \u00b7 ${assignment.shift_date}`
                : (shiftDate ? `Add shift \u00b7 ${shiftDate}` : 'Add to roster')}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Cancel"
            >
              Cancel
            </button>
          </div>
          {error && (
            <div role="alert" className="mx-4 mb-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-4 px-4 pb-4">
          {/* Staff picker */}
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="ra-staff">Staff</label>
            <select
              id="ra-staff"
              value={form.staff_id}
              onChange={(e) => set('staff_id', e.target.value)}
              className="w-full rounded-lg border border-black/15 bg-white px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            >
              <option value="">— pick staff member —</option>
              {activeStaff.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {s.role ? ` · ${s.role}` : ''}
                  {s.hourly_wage != null ? ` · ${formatMoney(s.hourly_wage)}/hr` : ''}
                </option>
              ))}
            </select>
            {activeStaff.length === 0 && (
              <p className="mt-1 text-xs text-black/55">
                No active staff yet. Add staff in Settings → Staff first.
              </p>
            )}
          </div>

          {/* Shift times */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="ra-start">
                Start time
              </label>
              <input
                id="ra-start"
                type="time"
                value={form.start_time}
                onChange={(e) => set('start_time', e.target.value)}
                className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="ra-end">
                End time
              </label>
              <input
                id="ra-end"
                type="time"
                value={form.end_time}
                onChange={(e) => set('end_time', e.target.value)}
                className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
              />
            </div>
          </div>

          {/* Estimated cost (live) */}
          {estCost != null && (
            <div className="flex items-center justify-between rounded-lg bg-black/5 px-3 py-2">
              <span className="text-sm text-black/70">Estimated labor cost</span>
              <span className="text-xl font-medium tabular-nums">
                {formatMoney(estCost)}
              </span>
            </div>
          )}

          {/* Optional role override */}
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="ra-role">
              Role today (optional)
            </label>
            <input
              id="ra-role"
              type="text"
              value={form.role}
              onChange={(e) => set('role', e.target.value)}
              placeholder={pickedStaff?.role ? `default: ${pickedStaff.role}` : 'leave blank to use staff default'}
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
            <p className="mt-1 text-xs text-black/55">
              Useful when someone is covering a different role today.
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="ra-notes">
              Notes (optional)
            </label>
            <textarea
              id="ra-notes"
              rows={2}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="Came in to cover for Sue / split shift / etc."
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
          </div>

          {/* Status — edit mode only. New shifts default to 'scheduled'. */}
          {isEditMode && (
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="ra-status">
                Status
              </label>
              <select
                id="ra-status"
                value={form.status}
                onChange={(e) => set('status', e.target.value)}
                className="w-full rounded-lg border border-black/15 bg-white px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
              >
                <option value="scheduled">Scheduled</option>
                <option value="called_out">Called out</option>
                <option value="no_show">No show</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <p className="mt-1 text-xs text-black/55">
                Called out / no show / cancelled shifts don't count toward labor cost.
              </p>
            </div>
          )}

          {/* Remove button — edit mode only. Sits inside the scrollable
              body so the primary "Save" CTA in the sticky footer stays
              the visually loudest action. */}
          {isEditMode && onDelete && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={submitting || deleting}
              className="min-h-btn w-full rounded-lg border border-red-200 bg-white px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 active:bg-red-100 disabled:cursor-wait disabled:opacity-50"
            >
              {deleting ? 'Removing\u2026' : 'Remove this shift'}
            </button>
          )}
        </div>

        <div className="sticky bottom-0 z-10 border-t border-black/5 bg-white px-4 pt-3 safe-bottom">
          <button
            type="submit"
            disabled={submitting || deleting || (!isEditMode && activeStaff.length === 0)}
            className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-xl font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting
              ? (isEditMode ? 'Saving\u2026' : 'Adding\u2026')
              : (isEditMode ? 'Save changes' : 'Add to roster')}
          </button>
        </div>
      </form>
    </div>
  )
}

function emptyForm() {
  return {
    staff_id: '',
    start_time: '',
    end_time: '',
    role: '',
    notes: '',
    status: 'scheduled',
  }
}
